import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

export const slugify = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

let lockUntil = 0;
let pinnedId = null;
const barOffset = () => {
  const bar = document.querySelector('[data-testid="menu-jump-bar"]');
  return (bar ? parseFloat(getComputedStyle(bar).top) + bar.offsetHeight : 150) + 24;
};
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  lockUntil = Date.now() + 1200;
  pinnedId = id;
  const y = el.getBoundingClientRect().top + window.scrollY - barOffset();
  if (window.__lenis) window.__lenis.scrollTo(y);
  else window.scrollTo({ top: y, behavior: "smooth" });
}

export function useMenu() {
  const [cats, setCats] = useState(null);
  useEffect(() => {
    api.get("/menu").then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, []);
  return cats;
}

export function MenuJumpBar({ cats }) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const onScroll = () => {
      if (Date.now() < lockUntil) return;
      const limit = barOffset() + 8;
      const pinned = pinnedId && document.getElementById(pinnedId)?.getBoundingClientRect();
      if (pinned && pinned.top <= limit + 40 && pinned.bottom > limit) return setActive(pinnedId);
      pinnedId = null;
      let best = null; let bestTop = -Infinity;
      cats.forEach((c) => {
        const el = document.getElementById(`menu-${slugify(c.name)}`);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top <= limit && top > bestTop) { best = el.id; bestTop = top; }
      });
      setActive(best);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [cats]);
  return (
    <nav className="sticky top-[76px] z-30 border-b border-ink bg-white/95 backdrop-blur md:top-[84px]" data-testid="menu-jump-bar">
      <div className="mx-auto flex max-w-[1400px] gap-2 overflow-x-auto px-4 py-3 md:flex-wrap md:px-8" style={{ scrollbarWidth: "none" }}>
        {cats.map((c) => {
          const id = `menu-${slugify(c.name)}`;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => { setActive(id); scrollToId(id); }}
              data-testid={`jump-${slugify(c.name)}`}
              className={`shrink-0 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${active === id ? "border-ink bg-ink text-bone" : "border-ink/40 text-ink hover:bg-ink hover:text-bone"}`}
            >
              {c.name}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function MenuCategoryBlock({ cat, index }) {
  return (
    <motion.section
      id={`menu-${slugify(cat.name)}`}
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45 }}
      className="scroll-mt-40 border-2 border-ink bg-white p-6 shadow-hard md:p-8"
      data-testid={`menu-category-${slugify(cat.name)}`}
    >
      <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">{String(index + 1).padStart(2, "0")}</div>
      <h2 className="font-rye text-3xl uppercase leading-none md:text-4xl">{cat.name}</h2>
      {cat.note && <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60">{cat.note}</p>}
      <ul className="mt-5 divide-y divide-dashed divide-ink/30">
        {cat.items.map((it) => (
          <li key={it.id} className="flex items-baseline gap-3 py-3" data-testid="menu-item">
            <div className="flex-1">
              <div className="font-alfa text-base uppercase leading-tight md:text-lg">{it.name}</div>
              {it.desc && <div className="mt-1 font-serif text-sm text-ink/70">{it.desc}</div>}
            </div>
            {it.price && <div className="font-mono text-sm font-bold tabular-nums" data-testid="menu-item-price">{it.price}</div>}
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

export function MenuBoard({ cats }) {
  if (!cats.length) return <p className="font-serif text-ink/60">Menu coming soon.</p>;
  return (
    <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2" data-testid="menu-board">
      {cats.map((c, i) => <MenuCategoryBlock key={c.id} cat={c} index={i} />)}
    </div>
  );
}
